import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

interface QueryValidity {
  parens: boolean;
  quotes: boolean;
}

@Component({
  selector: 'app-search-bar',
  standalone: false,
  templateUrl: './search-bar.html',
  styleUrl: './search-bar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchBar), multi: true },
  ],
})
export class SearchBar implements ControlValueAccessor {
  placeholder = input('');

  readonly value = signal('');
  readonly disabled = signal(false);

  /** A "quoted phrase" or a bare AND/OR/NOT whichever alternative
   *  matches first "wins" that span of text, so an operator word sitting
   *  inside a quoted phrase is never separately highlighted as one. */
  private static readonly TOKEN_RE = /("(?:[^"\\]|\\.)*")|\b(AND|OR|NOT)\b/g;

  /** Splits a query into whitespace-delimited tokens, keeping a "quoted
   *  phrase" together as one token even though it contains spaces
   *  used by checkOperatorGrammar, which only needs to tell an operator
   *  token apart from everything else (words, phrases, parens run
   *  together with a word, ...), not parse any of it further. */
  private static readonly QUERY_TOKEN_RE = /"(?:[^"\\]|\\.)*"|\S+/g;
  private static readonly BINARY_OPS = new Set(['AND', 'OR']);
  private static readonly ALL_OPS = new Set(['AND', 'OR', 'NOT']);

  readonly highlightedHtml = computed(() => this.highlight(this.value()));

  readonly validityMessage = computed(() => {
    const query = this.value();
    if (!query.trim()) return '';

    const balance = this.checkBalance(query);
    if (!balance.parens && !balance.quotes) return 'Unbalanced parentheses and quotes.';
    if (!balance.parens) return 'Unbalanced parentheses.';
    if (!balance.quotes) return 'Unbalanced quotes.';

    return this.checkOperatorGrammar(query) ?? '';
  });

  readonly isValid = computed(() => this.validityMessage() === '');

  private onChangeFn: (value: string) => void = () => {};
  private onTouchedFn: () => void = () => {};

  onInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.value.set(value);
    this.onChangeFn(value);
  }

  onBlur(): void {
    this.onTouchedFn();
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  private highlight(query: string): string {
    if (!query) return '';
    let html = '';
    let lastIndex = 0;
    for (const match of query.matchAll(SearchBar.TOKEN_RE)) {
      html += this.escapeHtml(query.slice(lastIndex, match.index));
      html += match[1]
        ? `<span class="search-bar__phrase">${this.escapeHtml(match[1])}</span>`
        : `<span class="search-bar__op search-bar__op--${match[2].toLowerCase()}">${match[2]}</span>`;
      lastIndex = match.index! + match[0].length;
    }
    html += this.escapeHtml(query.slice(lastIndex));
    return html.replace(/\n/g, '<br>');
  }

  /** Text-node escaping only (&/</>) this string is never used as an
   *  attribute value, and Angular's own [innerHTML] sanitizer is still
   *  the backstop against anything this misses. */
  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private checkBalance(query: string): QueryValidity {
    let depth = 0;
    let parens = true;
    for (const ch of query) {
      if (ch === '(') depth++;
      if (ch === ')') depth--;
      if (depth < 0) parens = false;
    }
    if (depth > 0) parens = false;

    const quoteCount = (query.match(/"/g) ?? []).length;
    return { parens, quotes: quoteCount % 2 === 0 };
  }

  private checkOperatorGrammar(query: string): string | null {
    const tokens: string[] = query.match(SearchBar.QUERY_TOKEN_RE) ?? [];
    const isOp = (t: string) => SearchBar.ALL_OPS.has(t);

    if (isOp(tokens[0])) {
      return `A query can't start with ${tokens[0]} it needs a term before it.`;
    }
    if (isOp(tokens[tokens.length - 1])) {
      return `A query can't end with ${tokens[tokens.length - 1]} it needs a term after it.`;
    }

    for (let i = 0; i < tokens.length - 1; i++) {
      const a = tokens[i];
      const b = tokens[i + 1];
      if (!isOp(a) || !isOp(b)) continue;
      if (SearchBar.BINARY_OPS.has(a) && b === 'NOT') continue; // AND NOT / OR NOT
      return `"${a} ${b}" isn't a valid combination add a term between them, or use "${a} NOT".`;
    }

    return null;
  }
}
